<template>
  <div class="callback-container">
    <div class="card">
      <div class="icon">🔄</div>
      <h2>Completing Zoho authorization…</h2>
      <p v-if="loading">Processing the authorization response. Please wait…</p>
      <p v-else-if="success" class="success">Linked successfully! Redirecting…</p>
      <p v-else class="error">{{ error || 'Authorization failed.' }}</p>

      <div class="actions" v-if="!loading && !success">
        <router-link to="/agents" class="btn btn-secondary">Back to Agents</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getApiUrl } from '@/config/api'

export default {
  name: 'CallbackView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(true)
    const success = ref(false)
    const error = ref('')

    const completeZohoAuth = async () => {
      try {
        const code = route.query.code
        const stateParam = route.query.state
        // Prefer agent_id from explicit query param; fallback for legacy flows
        const agentId = route.query.agent_id || (stateParam && !String(stateParam).startsWith('zoho') ? stateParam : '')

        if (!code || !agentId) {
          throw new Error('Missing code or agent_id in callback URL')
        }

        const token = localStorage.getItem('token')
        const payload = {
          code: String(code),
          agent_id: String(agentId),
          state: String(stateParam || 'zoho')
        }

        const resp = await fetch(getApiUrl('/api/agents/tools/zoho/token'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(payload)
        })

        if (!resp.ok) {
          let errMsg = 'Token exchange failed.'
          try {
            const d = await resp.json()
            errMsg = d.error || d.message || errMsg
          } catch {}
          throw new Error(errMsg)
        }

        success.value = true
        // Short delay for UX then redirect to /agents
        setTimeout(() => router.replace('/agents'), 500)
      } catch (e) {
        console.error('Callback error:', e)
        error.value = e?.message || 'Authorization failed.'
        success.value = false
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      completeZohoAuth()
    })

    return {
      loading,
      success,
      error
    }
  }
}
</script>

<style scoped>
.callback-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  padding: 2rem;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  padding: 2rem;
  max-width: 560px;
  width: 100%;
  text-align: center;
}

.icon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
}

h2 {
  margin: 0 0 0.5rem;
  color: #2c3e50;
}

p {
  color: #6c757d;
  margin: 0.25rem 0 1rem;
}

.success { color: #2e7d32; }
.error { color: #c62828; }

.actions { margin-top: 1rem; }

.btn {
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  margin-right: 0.5rem;
}

.btn-secondary {
  background: #f1f3f5;
  color: #495057;
  border: 1px solid #e9ecef;
}
</style>

